import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  DEFAULT_ORGANIZATION_CATEGORY,
  Organization,
  ORGANIZATION_CATEGORY,
  SupportedOrganizationCategory,
} from '@app/types/organization';
import { hasScoutLimit, isAgencyCategory } from '@app/utils/organization';

/** 電話番号(ハイフンなし) */
const PHONE_NUMBER_REGEX = /^0[0-9]{9,12}$/;

export interface OrganizationFormData {
  access: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  assigneeEmail: string;
  assigneeName: string;
  certificateImageUri: string;
  customFields: {
    access: string;
    addressByCity: string;
    addressLine4: string;
    clinicAppealMessage: string;
    clinicUrl: string;
    /** 会社電話番号。人材紹介企業のみ入力 */
    companyPhoneNumber: string;
    department: string;
    electronicChartMaker: string;
    electronicCharts: string[];
    equipmentOther: string;
    equipments: string[];
    mainImages: string;
    /** 組織区分。区分によって入力項目が変化する */
    organizationCategory: SupportedOrganizationCategory;
    ownerName: string;
    ownerUserId: string;
    /** 有料職業紹介番号。人材紹介企業のみ入力 */
    recruitmentLicenseNo: string;
    /** 代表者名。人材紹介企業のみ入力 */
    representativeName: string;
    scoutCount: number;
    scoutExpirationDate?: string;
    scoutMaxCount: number;
  };
  description: string;
  iconImageUri: string;
  logoImageUri: string;
  name: string;
  ownerId: string;
  phoneNumber: string;
  postalCode: string;
  privacyPolicy: boolean;
  scoutCountReset: boolean;
  shouldSkipValidation: boolean;
  userAgreement: boolean;
}
export const ORGANIZATION_FORM_DEFAULT_VALUES: OrganizationFormData = {
  access: '',
  addressLine1: '',
  addressLine2: '',
  addressLine3: '',
  assigneeEmail: '',
  assigneeName: '',
  certificateImageUri: '',
  customFields: {
    access: '',
    addressByCity: '',
    addressLine4: '',
    clinicAppealMessage: '',
    clinicUrl: '',
    companyPhoneNumber: '',
    department: '',
    electronicChartMaker: '',
    electronicCharts: [],
    equipmentOther: '',
    equipments: [],
    mainImages: '',
    organizationCategory: DEFAULT_ORGANIZATION_CATEGORY,
    ownerName: '',
    ownerUserId: '',
    recruitmentLicenseNo: '',
    representativeName: '',
    scoutCount: 0,
    scoutExpirationDate: '',
    scoutMaxCount: 0,
  },
  description: '',
  iconImageUri: '',
  logoImageUri: '',
  name: '',
  ownerId: '0000',
  phoneNumber: '',
  postalCode: '',
  privacyPolicy: false,
  scoutCountReset: false,
  shouldSkipValidation: false,
  userAgreement: false,
};

/** Organization interface の Object を OrganizationFormData に mapping する */
export const mappingOrganizationFormData = (
  editTarget: Organization
): OrganizationFormData => {
  const mapped = Object.entries(ORGANIZATION_FORM_DEFAULT_VALUES).reduce(
    (mapping, [key, value]) => {
      const matched = Object.entries(editTarget).find(
        ([targetKey]) => targetKey === key
      );
      // 型の確認
      if (!matched || typeof matched[1] !== typeof value) {
        return mapping;
      }
      return { ...mapping, [key]: matched[1] };
    },
    { ...ORGANIZATION_FORM_DEFAULT_VALUES } as OrganizationFormData
  );
  return {
    ...mapped,
    // NOTE:customFieldsはキーごと置き換わるため、既存データに無いキーが欠落する。
    // デフォルト値とマージして常に全キーを持たせる
    customFields: {
      ...ORGANIZATION_FORM_DEFAULT_VALUES.customFields,
      ...editTarget.customFields,
    },
  };
};

export enum Gender {
  Female = 'female',
  Male = 'male',
  None = '',
}

export enum GenderSelf {
  Female = '女性',
  Male = '男性',
  NoAnswer = '無回答',
  None = '',
  Other = 'その他',
}

export interface UserInfoFormData {
  customFields: {
    birthday: string;
    familyName: string;
    familyNameKana: string;
    firstName: string;
    firstNameKana: string;
    gender: Gender;
    genderSelf: GenderSelf;
  };
}

const UserInfoDefaultValues: UserInfoFormData = {
  customFields: {
    birthday: '',
    familyName: '',
    familyNameKana: '',
    firstName: '',
    firstNameKana: '',
    gender: Gender.None,
    genderSelf: GenderSelf.None,
  },
};

export const OrganizationFormValidator = z.object({
  addressLine1: z.string().min(1, { message: '都道府県を入力してください' }),
  addressLine2: z.string().min(1, { message: '市町村区を入力してください' }),
  addressLine3: z.string().min(1, { message: '町名・番地を入力してください' }),
  customFields: z.object({
    access: z.string().min(1, { message: 'アクセス情報を入力してください' }),
    addressByCity: z.string(),
    addressLine4: z.string(),
    clinicAppealMessage: z
      .string()
      .min(1, { message: '医療機関の一言アピールは必須です' })
      .max(200, {
        message: '医療機関の一言アピールは200文字以内で入力してください',
      }),
    clinicUrl: z.string(),
    // NOTE:人材紹介企業のみ必須。OrganizationAdminFormValidatorでチェックする
    companyPhoneNumber: z.string().optional(),
    department: z.string().min(1, { message: '標榜科目を入力してください' }),
    electronicChartMaker: z.string(),
    electronicCharts: z
      .array(z.string())
      .refine((value) => value.length, { message: 'カルテを選択してください' }),

    equipmentOther: z.string(),

    equipments: z.array(z.string()),

    mainImages: z.string(),

    // NOTE:未設定(既存データ)は医療機関として扱う
    organizationCategory: z
      .nativeEnum(ORGANIZATION_CATEGORY)
      .default(DEFAULT_ORGANIZATION_CATEGORY),

    ownerName: z.string().min(1, { message: 'オーナー名を入力してください' }),
    // NOTE:人材紹介企業のみ必須。OrganizationAdminFormValidatorでチェックする
    recruitmentLicenseNo: z.string().optional(),
    // NOTE:人材紹介企業のみ必須。OrganizationAdminFormValidatorでチェックする
    representativeName: z.string().optional(),
  }),
  description: z.string(),
  iconImageUri: z.string(),
  logoImageUri: z.string(),
  name: z.string().min(1, { message: '医療機関名を入力してください' }),
  ownerId: z.string().min(1, { message: 'ownerIdを入力してください' }),
  phoneNumber: z
    .string()
    .min(1, { message: '担当者連絡先電話番号を入力してください' })
    .regex(PHONE_NUMBER_REGEX, {
      message: '担当者連絡先電話番号はハイフンなしの数字を入力してください',
    }),
  postalCode: z
    .string()
    .regex(/^\d{7}$/, { message: '郵便番号を入力してください' }),
  privacyPolicy: z.boolean().refine((value) => value, {
    message: 'プライバシーポリシーに同意する必要があります',
  }),
  userAgreement: z
    .boolean()
    .refine((value) => value, { message: '利用規約に同意する必要があります' }),
});

export const OrganizationAdminFormValidator = OrganizationFormValidator.extend({
  customFields: z.object({
    ...OrganizationFormValidator.shape.customFields.shape,
    // NOTE:以下は医療機関のみ必須。組織区分ごとの必須チェックはsuperRefineで行う
    access: z.string(),
    clinicAppealMessage: z.string().max(200, {
      message: '医療機関の一言アピールは200文字以内で入力してください',
    }),
    department: z.string(),
    electronicCharts: z.array(z.string()),
    ownerName: z.string(),
    scoutExpirationDate: z.string().optional(),
    scoutMaxCount: z.preprocess(
      (v) => String(v),
      z
        .string()
        .nonempty({ message: '上限スカウト数を入力してください' })
        .regex(/^\d+$/, {
          message: '上限スカウト数は数値を入力してください',
        })
        .transform(Number)
    ),
  }),
  // NOTE:医療機関のみ必須。人材紹介企業は法人名(description)をnameとして登録する
  name: z.string(),
  scoutCountReset: z.boolean(),
}).superRefine((value, ctx) => {
  const { customFields, description, logoImageUri, name } = value;
  const addRequiredIssue = (path: string, message: string) => {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [path] });
  };
  // NOTE:区分ごとのチェックと同じsuperRefine内で行い、エラーを一度に表示させる
  if (
    hasScoutLimit(customFields.scoutMaxCount) &&
    !customFields.scoutExpirationDate
  ) {
    addRequiredIssue(
      'customFields.scoutExpirationDate',
      '上限スカウト有効期限を入力してください'
    );
  }
  // 人材紹介企業のみの入力項目
  if (isAgencyCategory(customFields.organizationCategory)) {
    if (!description) {
      addRequiredIssue('description', '法人名を入力してください');
    }
    if (!customFields.representativeName) {
      addRequiredIssue(
        'customFields.representativeName',
        '代表者名を入力してください'
      );
    }
    if (!customFields.companyPhoneNumber) {
      addRequiredIssue(
        'customFields.companyPhoneNumber',
        '会社電話番号を入力してください'
      );
    } else if (!PHONE_NUMBER_REGEX.test(customFields.companyPhoneNumber)) {
      addRequiredIssue(
        'customFields.companyPhoneNumber',
        '会社電話番号はハイフンなしの数字を入力してください'
      );
    }
    if (!customFields.recruitmentLicenseNo) {
      addRequiredIssue(
        'customFields.recruitmentLicenseNo',
        '有料職業紹介番号を入力してください'
      );
    }
    if (!customFields.clinicUrl) {
      addRequiredIssue('customFields.clinicUrl', 'Webページを入力してください');
    }
    if (!logoImageUri) {
      addRequiredIssue(
        'logoImageUri',
        '企業ロゴ写真をアップロードしてください'
      );
    }
    return;
  }
  // 医療機関のみの入力項目
  if (!name) {
    addRequiredIssue('name', '医療機関名を入力してください');
  }
  if (!customFields.access) {
    addRequiredIssue('customFields.access', 'アクセス情報を入力してください');
  }
  if (!customFields.department) {
    addRequiredIssue('customFields.department', '標榜科目を入力してください');
  }
  if (!customFields.ownerName) {
    addRequiredIssue('customFields.ownerName', 'オーナー名を入力してください');
  }
  if (!customFields.electronicCharts.length) {
    addRequiredIssue(
      'customFields.electronicCharts',
      'カルテを選択してください'
    );
  }
  if (!customFields.clinicAppealMessage) {
    addRequiredIssue(
      'customFields.clinicAppealMessage',
      '医療機関の一言アピールは必須です'
    );
  }
});

const UserInfoFormValidator = z.object({
  customFields: z.object({
    birthday: z.string().refine(
      (value) => {
        const parts = value.split('/');
        return parts.length === 3 && parts.every((part) => part !== '');
      },
      {
        message: '生年月日を入力してください',
      }
    ),
    familyName: z.string().min(1, { message: '姓を入力してください' }),
    familyNameKana: z
      .string()
      .min(1, { message: '姓(かな)を入力してください' })
      .refine((value) => /^[ぁ-んー\s]+$/u.test(value), {
        message: '姓(かな)はひらがなのみで入力してください',
      }),
    firstName: z.string().min(1, { message: '名を入力してください' }),
    firstNameKana: z
      .string()
      .min(1, { message: '名(かな)を入力してください' })
      .refine((value) => /^[ぁ-んー\s]+$/u.test(value), {
        message: '名(かな)はひらがなのみで入力してください',
      }),
    gender: z.string().min(1, { message: '性別を選択してください' }),
    genderSelf: z.string(),
  }),
});

export const RegistrationForm = {
  defaultValues: ORGANIZATION_FORM_DEFAULT_VALUES,
  resolver: zodResolver(OrganizationFormValidator),
  schema: OrganizationFormValidator,
} as const;

export const UseInfoForm = {
  defaultValues: UserInfoDefaultValues,
  resolver: zodResolver(UserInfoFormValidator),
  schema: UserInfoFormValidator,
} as const;
